import { CSSProperties, ReactNode } from 'react';
import clsx from 'clsx';

import { SingleSelect, SingleSelectProps } from '../selects/SingleSelect';

import styles from './comparison-select.module.scss';

export type ComparisonSelectOption = {
  label: string;
  value: string;
};

export type ComparisonSelectClassNames = {
  root?: string;
  baseDropdown?: string;
  comparisonDropdown?: string;
};

export type ComparisonSelectProps = {
  baseLabel?: ReactNode | ReactNode[];
  baseValue: string | null;
  classNames?: ComparisonSelectClassNames;
  comparisonLabel?: ReactNode | ReactNode[];
  comparisonValue: string | null;
  data: ComparisonSelectOption[];
  /** Overrides `data` for the base dropdown only. */
  baseData?: ComparisonSelectOption[];
  /** Overrides `data` for the comparison dropdown only. */
  comparisonData?: ComparisonSelectOption[];
  disabled?: boolean;
  loading?: boolean;
  onBaseChange?: (value: string | null) => void;
  onComparisonChange?: (value: string | null) => void;
  placeholder?: string;
  separator?: string;
  style?: CSSProperties;
  dynamicWidthSettings?: SingleSelectProps<string>['dynamicWidthSettings'];
};

export function ComparisonSelect({
  baseLabel,
  baseValue,
  classNames,
  comparisonLabel,
  comparisonValue,
  data,
  baseData,
  comparisonData,
  disabled = false,
  loading = false,
  onBaseChange,
  onComparisonChange,
  placeholder,
  separator = 'VS',
  style,
  dynamicWidthSettings,
}: ComparisonSelectProps) {
  return (
    <div
      className={clsx(styles['comparison-select'], classNames?.root)}
      style={style}
      data-testid="comparison-select"
    >
      <div
        className={clsx(
          styles['comparison-select__dropdown'],
          classNames?.baseDropdown
        )}
      >
        <SingleSelect<string>
          clearable={false}
          data={baseData ?? data}
          disabled={disabled}
          dynamicWidthSettings={dynamicWidthSettings}
          label={baseLabel}
          loading={loading}
          onChange={onBaseChange}
          placeholder={placeholder}
          value={baseValue}
        />
      </div>
      <span
        className={styles['comparison-select__separator']}
        aria-disabled={disabled}
      >
        {separator}
      </span>
      <div
        className={clsx(
          styles['comparison-select__dropdown'],
          classNames?.comparisonDropdown
        )}
      >
        <SingleSelect<string>
          clearable={false}
          data={comparisonData ?? data}
          disabled={disabled}
          dynamicWidthSettings={dynamicWidthSettings}
          label={comparisonLabel}
          loading={loading}
          onChange={onComparisonChange}
          placeholder={placeholder}
          value={comparisonValue}
        />
      </div>
    </div>
  );
}
